<template>
  <div class="relative w-full h-96 md:h-[500px] lg:h-[600px] overflow-hidden">
    <!-- Carousel container -->
    <div 
      class="flex transition-transform duration-1000 ease-in-out h-full"
      :style="{ transform: `translateX(-${currentIndex * 100}%)` }"
    >
      <div 
        v-for="(image, index) in images" 
        :key="index"
        class="w-full h-full flex-shrink-0 relative"
      >
        <picture class="block w-full h-full">
          <source
            v-if="image.webp"
            type="image/webp"
            :srcset="image.webp"
          />
          <img
            :src="image.src"
            :alt="`Photo ${index + 1}`"
            class="w-full h-full object-cover"
            :loading="index === 0 ? 'eager' : 'lazy'"
            :fetchpriority="index === 0 ? 'high' : 'auto'"
            decoding="async"
          />
        </picture>
        <!-- Overlay gradient -->
        <div class="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent"></div>
        
            <!-- Content overlay -->
            <div class="absolute inset-0 flex items-center justify-center">
              <div class="text-center text-white px-4 animate-fade-in">
                <h1 class="hero-title">
                  Royale Harmonie<br>
                  <span class="bg-gradient-to-r from-gold-400 via-gold-300 to-gold-500 bg-clip-text text-transparent">Émulation de Stavelot</span>
                </h1>
                <!--
                <p class="hero-subtitle"></p>
                -->
                <div class="flex flex-col sm:flex-row gap-4 justify-center mt-8">
                  <router-link 
                    to="/performances" 
                    class="btn-primary bg-gradient-to-r from-gold-400 to-gold-300 hover:from-gold-500 hover:to-gold-400 text-navy-900 font-bold px-8 py-4 rounded-2xl text-lg shadow-2xl hover:shadow-gold-500/25 transition-all duration-300 hover:scale-105"
                  >
                    Notre agenda
                  </router-link>
                  <router-link 
                    to="/history" 
                    class="btn-secondary bg-white/20 backdrop-blur-sm hover:bg-white/30 text-white border border-white/30 font-bold px-8 py-4 rounded-2xl text-lg shadow-xl hover:shadow-white/25 transition-all duration-300 hover:scale-105"
                  >
                    Notre histoire
                  </router-link>
                </div>
              </div>
            </div>
      </div>
    </div>

    <!-- Barre de navigation : sur mobile flèches + points ensemble en bas, sur desktop flèches sur les côtés -->
    <div class="absolute bottom-4 left-0 right-0 flex items-center justify-center gap-3 px-4 md:px-0">
      <!-- Flèches visibles sur mobile (dans la barre) -->
      <button 
        @click="previousSlide"
        class="flex md:hidden p-2.5 rounded-xl bg-white/20 backdrop-blur-md border border-white/30 text-white transition-all duration-300 hover:bg-white/30 active:scale-95"
        aria-label="Photo précédente"
      >
        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"/>
        </svg>
      </button>
      <div class="flex space-x-2">
        <button 
          v-for="(image, index) in images" 
          :key="index"
          @click="goToSlide(index)"
          class="w-3 h-3 rounded-full transition-all duration-300"
          :class="currentIndex === index ? 'bg-white' : 'bg-white/50 hover:bg-white/75'"
          :aria-label="`Aller à la photo ${index + 1}`"
        ></button>
      </div>
      <button 
        @click="nextSlide"
        class="flex md:hidden p-2.5 rounded-xl bg-white/20 backdrop-blur-md border border-white/30 text-white transition-all duration-300 hover:bg-white/30 active:scale-95"
        aria-label="Photo suivante"
      >
        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
        </svg>
      </button>
    </div>

    <!-- Flèches desktop uniquement (côtés) -->
    <button 
      @click="previousSlide"
      class="hidden md:flex absolute left-4 top-1/2 transform -translate-y-1/2 p-4 rounded-2xl bg-gradient-to-r from-primary-600/90 to-primary-700/90 backdrop-blur-md border border-primary-400/30 text-white transition-all duration-300 hover:from-primary-500 hover:to-primary-600 hover:scale-110 hover:shadow-xl shadow-lg items-center justify-center"
      aria-label="Photo précédente"
    >
      <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"/>
      </svg>
    </button>
    <button 
      @click="nextSlide"
      class="hidden md:flex absolute right-4 top-1/2 transform -translate-y-1/2 p-4 rounded-2xl bg-gradient-to-r from-primary-600/90 to-primary-700/90 backdrop-blur-md border border-primary-400/30 text-white transition-all duration-300 hover:from-primary-500 hover:to-primary-600 hover:scale-110 hover:shadow-xl shadow-lg items-center justify-center"
      aria-label="Photo suivante"
    >
      <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
      </svg>
    </button>
  </div>
</template>

<script>
import img150Webp from '../assets/carousel/150.webp'
import img150Jpg from '../assets/carousel/150-resized.jpg'
import imgArlonWebp from '../assets/carousel/arlon-1.webp'
import imgArlon from '../assets/carousel/arlon-1.jpg'
import imgLaetareWebp from '../assets/carousel/Laetare-1.webp'
import imgLaetareJpg from '../assets/carousel/Laetare-1.jpeg'
import imgNivezeWebp from '../assets/carousel/niveze-1.webp'
import imgNivezeJpg from '../assets/carousel/niveze-1-resized.jpg'

export default {
  name: 'PhotoCarousel',
  data() {
    return {
      currentIndex: 0,
      images: [
        { webp: img150Webp, src: img150Jpg },
        { webp: imgArlonWebp, src: imgArlon },
        { webp: imgLaetareWebp, src: imgLaetareJpg },
        { webp: imgNivezeWebp, src: imgNivezeJpg },
      ],
      interval: null
    }
  },
  mounted() {
    this.startAutoSlide()
  },
  beforeUnmount() {
    this.stopAutoSlide()
  },
  methods: {
    startAutoSlide() {
      this.interval = setInterval(() => {
        this.nextSlide()
      }, 5000) // Change d'image toutes les 5 secondes
    },
    stopAutoSlide() {
      if (this.interval) {
        clearInterval(this.interval)
        this.interval = null
      }
    },
    nextSlide() {
      this.currentIndex = (this.currentIndex + 1) % this.images.length
    },
    previousSlide() {
      this.currentIndex = this.currentIndex === 0 ? this.images.length - 1 : this.currentIndex - 1
    },
    goToSlide(index) {
      this.currentIndex = index
    }
  }
}
</script>

<style scoped>
/* Styles pour les transitions fluides */
.transition-transform {
  transition: transform 1000ms cubic-bezier(0.4, 0, 0.2, 1);
}

/* Styles spécifiques au carrousel */
</style>
